/**
 * SavSection — Section "Dossiers SAV" à l'intérieur d'un dossier véhicule.
 *
 * Vision : un véhicule = une histoire continue. Le SAV n'est PAS un onglet
 * permanent — cette section apparaît DANS le dossier une fois livré.
 * - Si aucun cas : une seule carte premium + CTA "Ouvrir un dossier SAV"
 * - Si des cas : liste chronologique (référence · status · responsable · date)
 *
 * L'ouverture d'un SAV navigue vers /app/sav/:id.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  LifeBuoy, Plus, ArrowUpRight, Clock, User, CheckCircle2, CircleDashed,
} from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { formatFrDate } from "@/lib/format";
import SavCreateModal from "./SavCreateModal";

const STATUS_TONE = {
  open:                    { bg: "bg-[#DBEAFE]", fg: "text-[#1E40AF]", Icon: CircleDashed },
  analysis_in_progress:    { bg: "bg-[#DBEAFE]", fg: "text-[#1E40AF]", Icon: CircleDashed },
  solution_in_progress:    { bg: "bg-[#FEF3C7]", fg: "text-[#92400E]", Icon: CircleDashed },
  appointment_scheduled:   { bg: "bg-[#EDE9FE]", fg: "text-[#5B21B6]", Icon: Clock },
  resolved:                { bg: "bg-[#DCFCE7]", fg: "text-[#166534]", Icon: CheckCircle2 },
  closed:                  { bg: "bg-[#EEF2F5]", fg: "text-[#6B7280]", Icon: CheckCircle2 },
};

const SavSection = ({ dossierId, eligible }) => {
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [error, setError] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.listSavForDossier(dossierId);
      setCases(r?.cases || []);
      setError(null);
    } catch (e) {
      setError(e.message || "Erreur de chargement");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!dossierId) return;
    load();
    // eslint-disable-next-line
  }, [dossierId]);

  // Silencieuse tant que le dossier n'est pas éligible (pas livré).
  if (!eligible) return null;

  return (
    <div className="wyvio-card p-6 lg:p-7" data-testid="sav-section">
      <div className="flex items-start justify-between gap-3 mb-4">
        <div className="flex items-center gap-2.5">
          <div className="h-9 w-9 rounded-full bg-[#111] text-[#C8FF2F] flex items-center justify-center">
            <LifeBuoy size={15} strokeWidth={2} />
          </div>
          <div>
            <h3 className="text-[15px] font-semibold text-[#111111] tracking-tight">
              Dossiers SAV
            </h3>
            <p className="mt-0.5 text-[11.5px] text-[#6B7280]">
              Suivi après-vente rattaché à ce véhicule
            </p>
          </div>
        </div>
        {cases.length > 0 && (
          <button
            type="button"
            data-testid="sav-open-new-btn"
            onClick={() => setShowModal(true)}
            className="h-9 inline-flex items-center gap-1.5 rounded-full bg-[#111] text-white px-3.5 text-[12px] font-medium hover:bg-black transition"
          >
            <Plus size={13} strokeWidth={2} />
            Nouveau
          </button>
        )}
      </div>

      {loading ? (
        <p className="py-10 text-center text-[12.5px] text-[#9CA3AF]" data-testid="sav-loading">
          Chargement…
        </p>
      ) : error ? (
        <p className="py-8 text-center text-[12.5px] text-[#B91C1C]">{error}</p>
      ) : cases.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-[#E5E7EB] p-8 flex flex-col items-center text-center gap-3" data-testid="sav-empty">
          <div className="h-11 w-11 rounded-full bg-[#F7F8FA] flex items-center justify-center">
            <LifeBuoy size={17} strokeWidth={1.75} className="text-[#9CA3AF]" />
          </div>
          <div>
            <p className="text-[13.5px] font-semibold text-[#111]">Aucun dossier SAV</p>
            <p className="mt-1 text-[12px] text-[#6B7280]">
              Ce véhicule n&apos;a fait l&apos;objet d&apos;aucun suivi après-vente.
            </p>
          </div>
          <button
            type="button"
            data-testid="sav-open-first-btn"
            onClick={() => setShowModal(true)}
            className="mt-1 h-10 inline-flex items-center gap-1.5 rounded-full bg-[#C8FF2F] text-[#111] px-5 text-[12.5px] font-semibold hover:brightness-95 transition"
          >
            <Plus size={13} strokeWidth={2.25} />
            Ouvrir un dossier SAV
          </button>
        </div>
      ) : (
        <ul className="space-y-2">
          {cases.map((c) => {
            const tone = STATUS_TONE[c.status] || STATUS_TONE.open;
            const Icon = tone.Icon;
            return (
              <li key={c.id} data-testid={`sav-row-${c.id}`}>
                <Link
                  to={`/app/sav/${c.id}`}
                  className="flex items-center gap-3 p-3.5 rounded-2xl border border-[#EEF2F5] hover:border-[#111]/20 hover:shadow-sm transition group"
                >
                  <div className={`h-9 w-9 rounded-full ${tone.bg} ${tone.fg} flex items-center justify-center shrink-0`}>
                    <Icon size={14} strokeWidth={2} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-[13.5px] font-semibold text-[#111]">{c.reference}</span>
                      <span className={`text-[10.5px] font-medium ${tone.fg} ${tone.bg} rounded-full px-2 py-0.5`}>
                        {c.status_label}
                      </span>
                      {c.is_closed && (
                        <span className="text-[10.5px] text-[#9CA3AF]">clôturé</span>
                      )}
                    </div>
                    <p className="mt-0.5 text-[12px] text-[#6B7280] truncate">{c.title}</p>
                    <p className="mt-1 text-[10.5px] text-[#9CA3AF] flex items-center gap-2">
                      <User size={10} strokeWidth={2} />
                      {c.responsible?.name || "Non assigné"}
                      <span>·</span>
                      <Clock size={10} strokeWidth={2} />
                      {formatFrDate(c.opened_at)}
                    </p>
                  </div>
                  <ArrowUpRight
                    size={15}
                    strokeWidth={2}
                    className="text-[#9CA3AF] group-hover:text-[#111] transition"
                  />
                </Link>
              </li>
            );
          })}
        </ul>
      )}

      {showModal && (
        <SavCreateModal
          dossierId={dossierId}
          onClose={() => setShowModal(false)}
          onCreated={(payload) => {
            load();
            // Toast avec le magic link si l'accès client a été réactivé
            const link = payload?.magic_link;
            if (link && navigator?.clipboard) {
              navigator.clipboard.writeText(link).catch(() => {});
              toast?.success?.(
                "Dossier SAV ouvert · Lien client copié dans le presse-papier",
                { icon: "✓", duration: 5000 },
              );
            } else {
              toast?.success?.("Dossier SAV ouvert");
            }
          }}
        />
      )}
    </div>
  );
};

export default SavSection;
